import React from 'react';
import {
  TrophyIcon,
  TrendingUpIcon,
  AwardIcon,
  ClockIcon,
  FilterIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XIcon,
} from 'lucide-react';

const kidDateInput =
  'h-9 max-w-[11rem] rounded-xl border-2 border-sky-200 bg-white px-2.5 text-xs font-semibold text-slate-800 shadow-inner transition focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200/80 sm:text-sm';

const kidLabel = 'mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500 sm:text-xs';

/**
 * تاب الإحصائيات + ترتيب القسم — تصميم عصري مناسب للأطفال (5–15).
 */
function DailyExercisesStatsRankingSection({
  t,
  formatNumber,
  formatDate,
  formatAvgTime,
  activeTab,
  setActiveTab,
  exerciseStats,
  loadingStats,
  availableExercise,
  departmentLeaderboard,
  loadingDepartmentLeaderboard,
  showRankingFilter,
  setShowRankingFilter,
  rankingDateFrom,
  rankingDateTo,
  setRankingDateFrom,
  setRankingDateTo,
  appliedRankingDateFrom,
  appliedRankingDateTo,
  applyRankingDateFilter,
  clearRankingFilter,
  getMedalStyle,
}) {
  return (
    <section
      className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/90 shadow-[0_8px_0_0_rgba(15,23,42,0.06)] backdrop-blur-sm"
      aria-label={t('dailyExercises.statistics')}
    >
      <div className="border-b border-slate-200/90 bg-gradient-to-r from-sky-50/90 via-white to-amber-50/50 px-2 py-2 sm:px-3">
        <nav className="flex flex-wrap gap-1.5 sm:gap-2" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('departmentRanking')}
            className={`inline-flex min-h-[2.5rem] flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-extrabold transition sm:min-h-0 sm:flex-none sm:rounded-2xl sm:px-4 sm:py-2.5 sm:text-sm ${
              activeTab === 'departmentRanking'
                ? 'bg-gradient-to-b from-amber-300 to-amber-400 text-amber-950 shadow-[0_3px_0_0_rgba(180,83,9,0.35)]'
                : 'bg-white/70 text-slate-600 hover:bg-sky-50'
            }`}
          >
            <TrophyIcon className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" strokeWidth={2.25} />
            {t('dailyExercises.departmentRanking')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('statistics')}
            className={`inline-flex min-h-[2.5rem] flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-extrabold transition sm:min-h-0 sm:flex-none sm:rounded-2xl sm:px-4 sm:py-2.5 sm:text-sm ${
              activeTab === 'statistics'
                ? 'bg-gradient-to-b from-sky-400 to-sky-500 text-white shadow-[0_3px_0_0_rgba(3,105,161,0.4)]'
                : 'bg-white/70 text-slate-600 hover:bg-sky-50'
            }`}
          >
            <TrendingUpIcon className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" strokeWidth={2.25} />
            {t('dailyExercises.statistics')}
          </button>
        </nav>
      </div>

      <div className="p-4 sm:p-6">
        {activeTab === 'statistics' && (
          <>
            {exerciseStats ? (
              <>
                <h3 className="mb-4 flex items-center gap-2 text-base font-black text-slate-900 sm:text-lg">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
                    <TrendingUpIcon className="h-5 w-5" strokeWidth={2.25} />
                  </span>
                  {t('dailyExercises.statistics')}
                </h3>
                {exerciseStats.summary && (
                  <div className="mb-6 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
                    {[
                      {
                        label: t('dailyExercises.totalDays'),
                        value: formatNumber(exerciseStats.summary.totalDays),
                        tone: 'from-sky-100 to-cyan-50 border-sky-200 text-sky-900',
                      },
                      {
                        label: t('dailyExercises.totalScore'),
                        value: formatNumber(exerciseStats.summary.totalScore),
                        tone: 'from-emerald-100 to-green-50 border-emerald-200 text-emerald-900',
                      },
                      {
                        label: t('dailyExercises.avgScore'),
                        value: formatNumber(exerciseStats.summary.avgScore, 1),
                        tone: 'from-violet-100 to-fuchsia-50 border-violet-200 text-violet-900',
                      },
                      {
                        label: t('dailyExercises.bestTime'),
                        value: `${formatNumber(exerciseStats.summary.bestTime / 1000, 1)}s`,
                        tone: 'from-amber-100 to-orange-50 border-amber-200 text-amber-950',
                      },
                    ].map((card) => (
                      <div
                        key={card.label}
                        className={`rounded-2xl border-2 bg-gradient-to-br p-3 shadow-sm sm:p-4 ${card.tone}`}
                      >
                        <p className="text-[10px] font-bold uppercase tracking-wide opacity-80 sm:text-xs">{card.label}</p>
                        <p className="mt-1 text-xl font-black tabular-nums sm:text-2xl">{card.value}</p>
                      </div>
                    ))}
                  </div>
                )}
                {exerciseStats.dailyBests && exerciseStats.dailyBests.length > 0 && (
                  <div>
                    <h4 className="mb-3 flex items-center gap-2 text-sm font-extrabold text-slate-800 sm:text-base">
                      <AwardIcon className="h-5 w-5 text-amber-500" strokeWidth={2.25} />
                      {t('dailyExercises.dailyBests')}
                    </h4>
                    <ul className="space-y-2">
                      {exerciseStats.dailyBests.map((dailyBest, index) => {
                        const date = new Date(dailyBest.attemptDate);
                        const formattedDate = formatDate(date);
                        return (
                          <li
                            key={dailyBest.id}
                            className="flex items-center justify-between gap-3 rounded-2xl border-2 border-slate-100 bg-slate-50/80 px-3 py-2.5 sm:px-4"
                          >
                            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-200 text-xs font-black text-sky-900 sm:h-9 sm:w-9 sm:text-sm">
                                {formatNumber(index + 1)}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-xs font-bold text-slate-900 sm:text-sm">{formattedDate}</p>
                                <p className="text-[10px] font-semibold text-slate-500 sm:text-xs">
                                  {t('dailyExercises.attempts')}: {formatNumber(dailyBest.totalAttempts)}
                                </p>
                              </div>
                            </div>
                            <div className="shrink-0 text-end">
                              <p className="text-sm font-black text-emerald-600 sm:text-base">{formatNumber(dailyBest.bestScore)}</p>
                              <p className="flex items-center justify-end gap-0.5 text-[10px] font-semibold text-slate-500 sm:text-xs">
                                <ClockIcon className="h-3 w-3" />
                                {formatNumber(dailyBest.bestTimeMs / 1000, 1)}s
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {loadingStats && (
                  <div className="py-6 text-center">
                    <div className="mx-auto h-9 w-9 animate-spin rounded-full border-[3px] border-sky-200 border-t-sky-600" />
                  </div>
                )}
                {!exerciseStats.summary && !loadingStats && (
                  <p className="rounded-2xl border-2 border-dashed border-slate-200 py-8 text-center text-sm font-semibold text-slate-500">
                    {t('dailyExercises.noStatistics')}
                  </p>
                )}
              </>
            ) : (
              <p className="rounded-2xl border-2 border-dashed border-slate-200 py-8 text-center text-sm font-semibold text-slate-500">
                {t('dailyExercises.noStatistics')}
              </p>
            )}
          </>
        )}

        {activeTab === 'departmentRanking' && (
          <>
            <h3 className="mb-4 flex items-center gap-2 text-base font-black text-slate-900 sm:text-lg">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                <TrophyIcon className="h-5 w-5" strokeWidth={2.25} />
              </span>
              {t('dailyExercises.rankingInDepartment')}
            </h3>
            {!availableExercise && (
              <p className="rounded-2xl border-2 border-dashed border-amber-200 bg-amber-50/50 py-8 text-center text-sm font-semibold text-amber-900/80">
                {t('dailyExercises.noExerciseForRanking')}
              </p>
            )}
            {availableExercise && (
              <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRankingFilter((v) => !v)}
                    className="inline-flex items-center gap-1.5 rounded-xl border-2 border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-extrabold text-slate-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50 sm:px-3 sm:py-2 sm:text-xs"
                  >
                    <FilterIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    {showRankingFilter ? t('dailyExercises.hideFilter') : t('dailyExercises.showFilter')}
                    {showRankingFilter ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
                  </button>
                  {showRankingFilter && (appliedRankingDateFrom || appliedRankingDateTo) && (
                    <button
                      type="button"
                      onClick={clearRankingFilter}
                      className="inline-flex items-center gap-1 rounded-xl border-2 border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-extrabold text-red-700 transition hover:bg-red-100 sm:text-xs"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                      {t('dailyExercises.clearFilter')}
                    </button>
                  )}
                </div>
                {showRankingFilter && (
                  <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border-2 border-sky-100 bg-gradient-to-br from-sky-50/80 to-white p-3 sm:p-4">
                    <div className="min-w-0 flex-1 sm:max-w-[12rem]">
                      <label className={kidLabel}>{t('dailyExercises.dateFrom')}</label>
                      <input
                        type="date"
                        value={rankingDateFrom}
                        onChange={(e) => setRankingDateFrom(e.target.value)}
                        className={`w-full ${kidDateInput}`}
                      />
                    </div>
                    <div className="min-w-0 flex-1 sm:max-w-[12rem]">
                      <label className={kidLabel}>{t('dailyExercises.dateTo')}</label>
                      <input
                        type="date"
                        value={rankingDateTo}
                        onChange={(e) => setRankingDateTo(e.target.value)}
                        className={`w-full ${kidDateInput}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={applyRankingDateFilter}
                      className="h-9 shrink-0 rounded-xl bg-gradient-to-b from-sky-500 to-sky-600 px-4 text-xs font-extrabold text-white shadow-[0_3px_0_0_rgba(3,105,161,0.45)] transition hover:brightness-105 active:translate-y-px sm:h-9 sm:text-sm"
                    >
                      {t('dailyExercises.showResults')}
                    </button>
                  </div>
                )}
                {loadingDepartmentLeaderboard ? (
                  <div className="py-10 text-center">
                    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-[3px] border-amber-200 border-t-amber-600" />
                  </div>
                ) : departmentLeaderboard.length === 0 ? (
                  <p className="rounded-2xl border-2 border-dashed border-slate-200 py-8 text-center text-sm font-semibold text-slate-500">
                    {t('dailyExercises.noRankingData')}
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-2xl border-2 border-slate-100 bg-white shadow-inner">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-xs sm:text-sm">
                        <thead>
                          <tr className="border-b-2 border-slate-100 bg-gradient-to-r from-slate-50 to-sky-50/50">
                            <th className="px-3 py-2.5 font-extrabold text-slate-600 sm:px-4 sm:py-3">{t('dailyExercises.rank')}</th>
                            <th className="px-3 py-2.5 font-extrabold text-slate-600 sm:px-4">{t('dailyExercises.nameAndNumber')}</th>
                            <th className="px-3 py-2.5 font-extrabold text-slate-600 sm:px-4">{t('dailyExercises.daysPracticed')}</th>
                            <th className="px-3 py-2.5 font-extrabold text-slate-600 sm:px-4">{t('dailyExercises.totalScoreLabel')}</th>
                            <th className="px-3 py-2.5 font-extrabold text-slate-600 sm:px-4">{t('dailyExercises.avgTime')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {departmentLeaderboard.map((row) => {
                            const medal = getMedalStyle(row.rank);
                            return (
                              <tr
                                key={row.studentUserId ?? row.rank}
                                className={`transition-colors hover:bg-sky-50/50 ${medal.row}`}
                              >
                                <td className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3">
                                  <div className="flex items-center gap-1.5">
                                    {row.rank <= 3 ? (
                                      <>
                                        <AwardIcon className={`h-5 w-5 shrink-0 sm:h-6 sm:w-6 ${medal.icon}`} aria-hidden />
                                        <span className="font-black text-slate-900">{row.rank}</span>
                                      </>
                                    ) : (
                                      <span className="font-bold text-slate-700">{row.rank}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="max-w-[10rem] px-3 py-2.5 sm:max-w-none sm:px-4 sm:py-3">
                                  <span className="font-bold text-slate-900">
                                    {row.fullName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || '-'}
                                  </span>
                                  {row.studentNumber && (
                                    <span className="mt-0.5 block truncate text-[10px] font-semibold text-slate-500 sm:text-xs">
                                      {row.studentNumber}
                                    </span>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2.5 font-bold tabular-nums text-slate-700 sm:px-4">
                                  {row.daysPracticed ?? '-'}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2.5 font-black tabular-nums text-slate-900 sm:px-4">
                                  {row.totalScore != null ? formatNumber(Number(row.totalScore), 2) : '-'}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2.5 font-semibold tabular-nums text-slate-600 sm:px-4">
                                  {formatAvgTime(row.avgTimeMs)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default DailyExercisesStatsRankingSection;
